import { createClient } from '@supabase/supabase-js'
import { apiClient } from './api'

// Use new Supabase environment variables, fallback to legacy ones for compatibility
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || import.meta.env.VITE_API_URL || ''
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || import.meta.env.VITE_API_KEY || ''

// Stable storage adapter: reads "remember me" preference at call-time
// but presents a consistent object reference to Supabase to avoid lock warnings.
const rememberMeStorage = {
  getItem: (key: string): string | null => {
    const rememberMe = localStorage.getItem('scouta_remember_me') !== 'false'
    return (rememberMe ? window.localStorage : window.sessionStorage).getItem(key)
  },
  setItem: (key: string, value: string): void => {
    const rememberMe = localStorage.getItem('scouta_remember_me') !== 'false'
    ;(rememberMe ? window.localStorage : window.sessionStorage).setItem(key, value)
  },
  removeItem: (key: string): void => {
    const rememberMe = localStorage.getItem('scouta_remember_me') !== 'false'
    ;(rememberMe ? window.localStorage : window.sessionStorage).removeItem(key)
  },
}

export const backendClient = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    storage: rememberMeStorage,
  }
})

// Auth Helpers - Use Supabase directly for auth
export const auth = {
    signUp: async (email: string, password: string, name: string, role: string) => {
        const { data, error } = await backendClient.auth.signUp({
            email,
            password,
            options: {
                data: {
                    full_name: name,
                    role: role
                }
            }
        })
        if (error) throw error
        return data
    },

    signUpWithMetadata: async (email: string, password: string, metadata: Record<string, string>) => {
        const { data, error } = await backendClient.auth.signUp({
            email,
            password,
            options: {
                data: metadata
            }
        })
        if (error) throw error
        return data
    },

    signIn: async (email: string, password: string) => {
        const { data, error } = await backendClient.auth.signInWithPassword({
            email,
            password
        })
        if (error) throw error
        return data
    },

    signOut: async () => {
        const { error } = await backendClient.auth.signOut()
        if (error) throw error
    },

    getUser: async () => {
        const { data: { user }, error } = await backendClient.auth.getUser()
        if (error) return null
        return user
    },

    getProfile: async (userId: string) => {
        const data = await apiClient.getProfile(userId)
        return data
    },

    updateProfile: async (userId: string, updates: any) => {
        const data = await apiClient.updateProfile(userId, updates)
        return data
    },

    resetPassword: async (email: string) => {
        const redirectUrl = `${window.location.origin}/reset-password`
        const { data, error } = await backendClient.auth.resetPasswordForEmail(email, {
            redirectTo: redirectUrl,
        })
        if (error) throw error
        return data
    },

    updateUserPassword: async (newPassword: string) => {
        const { data, error } = await backendClient.auth.updateUser({
            password: newPassword
        })
        if (error) throw error
        return data
    },

    updateAccountProfile: async (updates: { fullName?: string; org?: string; avatarUrl?: string }) => {
        const metadata: Record<string, string> = {}

        if (typeof updates.fullName === 'string') {
            metadata.full_name = updates.fullName
        }

        if (typeof updates.org === 'string') {
            metadata.org = updates.org
        }

        if (typeof updates.avatarUrl === 'string') {
            metadata.avatar_url = updates.avatarUrl
        }

        if (Object.keys(metadata).length === 0) {
            return null
        }

        const { data, error } = await backendClient.auth.updateUser({
            data: metadata
        })
        if (error) throw error
        return data
    }
}

// Export the API client for direct use if needed
export { apiClient }
